import { delay, takeLatest, takeEvery } from 'redux-saga';
import { call, put, take, race, select, fork } from 'redux-saga/effects';
import { normalize } from 'normalizr';

import * as schemas from 'schemas';
import { getChatQuestionnaire } from 'selectors';
import * as actions from 'actions';
import * as api from 'api';
import { errorActionFrom } from 'utils/actions';
import { socketRequestWithErrorChecks } from 'utils/sagas';


export function* watchForFeedbackDecline() {
  while(true) { // eslint-disable-line no-constant-condition
    const action = yield take(actions.CHAT_QUESTIONNAIRE_UPDATE);

    if(action.chatQuestionnaireId === 'eos' && action.updates.declined) {
      const chatQuestionnaire = yield select(getChatQuestionnaire, action.chatQuestionnaireId);
      yield put(actions.chatQuestionnaireSubmit(chatQuestionnaire));
    }
  }
}

export function* updateChatQuestion(socket, action) {
  if(action.chatQuestionnaireId !== 'eos') {
    const chatQuestionnaire = yield select(getChatQuestionnaire, action.chatQuestionnaireId);
    yield call(socketRequestWithErrorChecks, socket, socket.chatQuestionnaireSubmit, action, chatQuestionnaire);
  }
}

export function filterUpdatedChatMessages(chatMessages, chatMessagesBuffer) {
  const { result, entities } = normalize(chatMessages, schemas.chatMessagesArraySchema);

  const filteredChatMessagesIds = result.filter(chatMessageId => {
    const chatMessage = entities.chatMessages[chatMessageId];
    const bufferedChatMessagesTexts = chatMessagesBuffer.map(bufferedChatMessage => bufferedChatMessage.text);

    return chatMessage.fromCounsellor || bufferedChatMessagesTexts.indexOf(chatMessage.text) < 0;
  });

  const filteredEntities = {
    chatMessages: {}
  };

  filteredChatMessagesIds.forEach(chatMessageId => filteredEntities.chatMessages[chatMessageId] = entities.chatMessages[chatMessageId]);

  return {
    chatMessagesIds: filteredChatMessagesIds,
    entities: filteredEntities,
    lastChatMessageId: result[result.length - 1]
  };
}

export function* refreshChatQueueRejectionExpiration(ban) {
  const expirationDate = new Date(ban.expiresAtTimestamp);

  while(true) { // eslint-disable-line no-constant-condition
    const error = new Error(ban.reason);
    error.isRejected = true;
    error.expirationDate = new Date(ban.expiresAtTimestamp);
    error.showInInfoBar = false;

    const errorAction = errorActionFrom(actions.chatEnqueue(), error);
    yield put(errorAction);
    yield call(delay, process.env.KOOTH_QUEUE_REJECTION_REFRESH_INTERVAL);

    if(Date.now() > expirationDate.getTime()) {
      yield put(actions.chatQueueRejectionExpire());
      break;
    }
  }
}

export function* watchForChatSessionRequests(socket) {
  while(true) { // eslint-disable-line no-constant-condition
    const action = yield take([
      actions.CHAT_ENQUEUE,
      actions.CHAT_ENQUEUE_CANCEL
    ]);

    if(action.type === actions.CHAT_ENQUEUE_CANCEL) {
      yield call(socketRequestWithErrorChecks, socket, socket.chatEnqueueCancel, action);
      continue;
    }

    const { request, cancelAction } = yield race({ // eslint-disable-line no-unused-vars
      request: socketRequestWithErrorChecks(socket, socket.chatEnqueue, action),
      cancelAction: take(actions.CHAT_ENQUEUE_CANCEL)
    });

    if(cancelAction) {
      yield call(socketRequestWithErrorChecks, socket, socket.chatEnqueueCancel, cancelAction);
    }
  }
}

export function* addChatMessage(socket, addAction) {
  const chatMessage = addAction.chatMessage;

  const updateAction = actions.chatMessagesUpdate({
    chatMessagesIds: [chatMessage.id],
    entities: {
      chatMessages: {
        [chatMessage.id]: chatMessage
      }
    }
  }, false);

  yield put(updateAction);
  yield put(actions.chatMessageEditingReset());

  try {
    yield call([socket, socket.chatSendMessage], {text: chatMessage.text});
  }
  catch(error) {
    yield put(actions.chatMessageRemove(chatMessage.id));
    yield put(actions.chatMessageEditingUpdateText({ text: chatMessage.text }));
    yield put(errorActionFrom(updateAction, error));
  }
}

export function* updateChatWritingIndicator(socket, updateAction) {
  const isWriting = updateAction.isWriting;
  if (isWriting) {
    yield call([socket, socket.chatStartWritingMessage]);
  } else {
    yield call([socket, socket.chatStopWritingMessage]);
  }
}

export function actionForReceivingChatDocument(panel) {
  const entity = {
    id: Date.now(),
    ...panel.data
  };

  const action = actions.chatDocumentsReceived({
    chatDocumentsIds: [entity.id],
    entities: {
      chatDocuments: {
        [entity.id]: entity
      }
    }
  });

  return action;
}

export function actionForReceivingChatWebsite(panel) {
  const entity = {
    id: Date.now(),
    ...panel.data
  };

  const action = actions.chatWebsitesReceived({
    chatWebsitesIds: [entity.id],
    entities: {
      chatWebsites: {
        [entity.id]: {
          ...panel.metadata,
          ...entity
        }
      }
    }
  });

  return action;
}

export function actionForReceivingGoals(panel, chatItems) {
  const filteredChatItems = chatItems.filter(chatItem => chatItem.goals);
  const receivedGoals = panel.data.map(goal => {
    return {
      ...goal,
      updatedAt: new Date(goal.updatedAt)
    };
  });
  let shouldUpdateGoals = true;

  if(filteredChatItems.length > 0) {
    const currentGoals = filteredChatItems[0].goals;
    shouldUpdateGoals = JSON.stringify(receivedGoals, removeUpdateAtReplacer) != JSON.stringify(currentGoals, removeUpdateAtReplacer);
  }

  if(shouldUpdateGoals) {
    const goalsIds = receivedGoals.map(goal => goal.id);
    const goalIdToObjectPairs = receivedGoals.map(goal => ({[goal.id]: goal}));
    const goals = Object.assign({}, ...goalIdToObjectPairs);

    const action = actions.goalsReceived({
      goalsIds,
      entities: {
        goals
      }
    });

    return action;
  }

  return null;
}

export function actionForReceivingChatQuestionnaire(panel, chatItems) {
  const receivedQuestionnaire = {
    ...panel.data,
    updatedAt: new Date(panel.data.updatedAt)
  };

  const filteredChatItems = chatItems.filter(chatItem => chatItem.chatQuestionnaire && chatItem.chatQuestionnaire.id === receivedQuestionnaire.id);
  let shouldUpdateChatQuestionnaire = true;

  if(filteredChatItems.length > 0) {
    const currentQuestionnaire = filteredChatItems[0].chatQuestionnaire;

    shouldUpdateChatQuestionnaire = JSON.stringify(receivedQuestionnaire, removeUpdateAtReplacer) != JSON.stringify(currentQuestionnaire, removeUpdateAtReplacer);
  }

  if(shouldUpdateChatQuestionnaire) {
    const { result, entities } = normalize(receivedQuestionnaire, schemas.chatQuestionnaireSchema);
    const action = actions.chatQuestionnairesReceived({ chatQuestionnairesIds: result, entities });

    return action;
  }

  return null;
}

function removeUpdateAtReplacer(key, value) {
  if(key === 'updatedAt') {
    return undefined;
  }

  return value;
}

export function manageChatActiveStateUpdate(panel, chatItems) {
  const actionCreatorForReceiving = {
    'DOCUMENT': actionForReceivingChatDocument,
    'WEBSITE': actionForReceivingChatWebsite,
    'GOALS': actionForReceivingGoals,
    'QUESTIONNAIRE': actionForReceivingChatQuestionnaire
  };

  const action = actionCreatorForReceiving[panel.type](panel, chatItems);

  return action;
}

export default function* backgroundSaga(socket, request) {
  yield [
    fork(takeLatest, actions.CHAT_QUESTIONNAIRE_SUBMIT, request(api.chatQuestionnaireSubmit, 'chatQuestionnaire')),
    fork(takeLatest, actions.CHAT_RESOURCES_FETCH, request(api.chatResourcesFetch, 'page')),
    fork(takeLatest, actions.CHAT_SCHEDULE_FETCH, request(api.chatScheduleFetch)),
    fork(takeLatest, actions.CHAT_QUESTION_UPDATE, updateChatQuestion, socket),
    fork(takeEvery, actions.CHAT_MESSAGE_ADD, addChatMessage, socket),
    fork(takeEvery, actions.CHAT_MESSAGE_UPDATE_WRITING_INDICATOR, updateChatWritingIndicator, socket),
    fork(watchForFeedbackDecline),
    fork(watchForChatSessionRequests, socket)
  ];
}



/** WEBPACK FOOTER **
 ** ./src/members/state/chat/sagas.js
 **/